import React, { useEffect, useState } from "react";
import { message, Spin } from "antd";
import { useDispatch } from "react-redux";
import { changeHeader } from '../../../redux/reducer/headerSlice';
import handleGet from "../../../utils/Get";
import handlePost from "../../../utils/Post";
import { BASE_URL } from "../../../utils/config";
import { useTranslation } from "react-i18next";

const SwapCrypto = () => {
  const dispatch = useDispatch();
  const user = JSON.parse(localStorage.getItem('user'));

  const [data, setData] = useState([]);
  const [fromAsset, setFromAsset] = useState('');
  const [toAsset, setToAsset] = useState('');
  const [amount, setAmount] = useState('');
  const [exchangeData, setExchangeData] = useState('');
  const [loading, setLoading] = useState(false);
  const [swapping, setSwapping] = useState(false);
  const [loader, setLoader] = useState(false);
  const [NotesData, setNotesData] = useState([]);

  const { t } = useTranslation();
  const { The, Trade, exchange, crypto, Swap, no, of, coins, network, fe, total } = t("dashboard", { returnObjects: true });

  useEffect(() => {
    const fetchUser = async () => {
      const res = await handleGet(`${BASE_URL}get/user/detail/${user?.id}`);
      if (res) setNotesData(res?.user);
    };
    fetchUser();
  }, []);

  useEffect(() => {
    dispatch(changeHeader(`${user?.name || "User"}, Swap Crypto`));
  }, [user, dispatch]);

  useEffect(() => {
    const fetchAssets = async () => {
      setLoading(true);
      try {
        const response = await handleGet(`${BASE_URL}active/user/assets`);
        setData(response?.assets || []);
      } catch (error) {
        console.error("Error fetching assets:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchAssets();
  }, []);

  const handleExchange = async (newAmount, newFrom, newTo) => {
    if (!newAmount || !newFrom || !newTo) return;
    setLoader(true);
    try {
      const response = await handlePost(`${BASE_URL}currencyexchange/onchange`, {
        amount: newAmount,
        from_asset_id: newFrom,
        to_asset_id: newTo,
      });
      if (response) setExchangeData(response?.data);
    } catch (error) {
      console.error("Error during exchange:", error);
    } finally {
      setLoader(false);
    }
  };

  const handleFromAssetChange = (e) => {
    setFromAsset(e.target.value);
    handleExchange(amount, e.target.value, toAsset);
  };

  const handleToAssetChange = (e) => {
    setToAsset(e.target.value);
    handleExchange(amount, fromAsset, e.target.value);
  };

  const handleAmountChange = (e) => {
    const val = e.target.value;
    setAmount(val);
    handleExchange(val, fromAsset, toAsset);
  };

  const submitSwap = async () => {
    if (NotesData?.detail?.is_swap === "false") {
      message.info(NotesData?.detail?.swap);
      return;
    }
    setSwapping(true);
    try {
      const response = await handlePost(`${BASE_URL}currency/exchange`, {
        amount,
        from_asset_id: fromAsset,
        to_asset_id: toAsset,
      });
      if (response) {
        setFromAsset('');
        setToAsset('');
        setAmount('');
        setExchangeData('');
      }
    } catch (error) {
      console.error("Error during swap submission:", error);
    } finally {
      setSwapping(false);
    }
  };

  const fromAssetObj = data.find(a => a.id === fromAsset);
  const toAssetObj   = data.find(a => a.id === toAsset);

  const summaryRows = [
    { label: `${no} ${of} ${coins}`, value: exchangeData?.coins },
    { label: `${network} ${fe}`,     value: exchangeData?.networkfees },
    { label: `${total}`,             value: exchangeData?.TotalAmount },
  ];

  return (
    <section className="w-full space-y-5">

      {/* ── Page heading ── */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.25em] text-slate-500">{The} {Trade}</p>
        <h1 className="text-2xl font-bold text-slate-100 mt-0.5 capitalize">{exchange} {crypto}</h1>
      </div>

      {/* ── Two-column layout ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">

        {/* ── LEFT: Swap form ── */}
        <div className="rounded-[26px]  px-3 py-3 bg-[#1e293b] shadow-[0_22px_60px_rgba(15,23,42,0.9)] p-6 space-y-3">

          <h2 className="text-white text-base font-semibold flex items-center gap-2 mb-1">
            ⇅ Swap Assets
          </h2>

          {/* SEND */}
          <div className="rounded-2xl bg-[#efe4c9] p-4">
            <p className="text-xs font-semibold uppercase tracking-widest text-stone-500 mb-2">Send</p>
            <div className="flex items-center gap-3">
              <input
                type="number"
                placeholder="0.00"
                value={amount}
                onChange={handleAmountChange}
                className="flex-1 min-w-0 bg-transparent border-none outline-none text-3xl font-semibold text-stone-800 placeholder-stone-400 w-full"
                style={{ appearance: 'textfield', MozAppearance: 'textfield', WebkitAppearance: 'none' }}
              />
              <div className="relative">
                <select
                  value={fromAsset}
                  onChange={handleFromAssetChange}
                  disabled={loading}
                  className="appearance-none bg-[#1e293b] text-slate-100 font-semibold text-sm rounded-xl pl-3 pr-8 py-2.5 border-none outline-none cursor-pointer min-w-[130px]"
                >
                  <option value="">{loading ? 'Loading…' : 'Select'}</option>
                  {data.map(a => (
                    <option key={a.id} value={a.id}>{a.name} ({a.amount})</option>
                  ))}
                </select>
                <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-xs">▾</span>
              </div>
            </div>
            {fromAssetObj && (
              <p className="text-xs text-stone-500 mt-2 font-medium">
                Balance: {fromAssetObj.amount} {fromAssetObj.short_name}
              </p>
            )}
          </div>

          {/* Swap divider */}
          <div className="flex justify-center -my-1 relative z-10">
            <button
              onClick={() => {
                const temp = fromAsset;
                setFromAsset(toAsset);
                setToAsset(temp);
                handleExchange(amount, toAsset, fromAsset);
              }}
              className="h-11 w-11 rounded-full bg-[#0f172a] border-2 border-slate-700 flex items-center justify-center text-white text-lg shadow-lg hover:rotate-180 transition-transform duration-300"
            >
              ⇅
            </button>
          </div>

          {/* RECEIVE */}
          <div className="rounded-2xl bg-[#cfe3df] p-4">
            <p className="text-xs font-semibold uppercase tracking-widest text-teal-700 mb-2">Receive</p>
            <div className="flex items-center gap-3">
              <div className="flex-1 min-w-0">
                {loader ? (
                  <div className="flex items-center gap-2">
                    <Spin size="small" />
                    <span className="text-teal-600 text-sm">Calculating…</span>
                  </div>
                ) : (
                  <p className="text-3xl font-semibold text-teal-900">
                    {exchangeData?.coins || <span className="text-teal-400/60">0.00</span>}
                  </p>
                )}
              </div>
              <div className="relative">
                <select
                  value={toAsset}
                  onChange={handleToAssetChange}
                  disabled={loading}
                  className="appearance-none bg-[#1e293b] text-slate-100 font-semibold text-sm rounded-xl pl-3 pr-8 py-2.5 border-none outline-none cursor-pointer min-w-[130px]"
                >
                  <option value="">{loading ? 'Loading…' : 'Select'}</option>
                  {data.filter(a => a.id !== fromAsset).map(a => (
                    <option key={a.id} value={a.id}>{a.name} ({a.amount})</option>
                  ))}
                </select>
                <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-xs">▾</span>
              </div>
            </div>
            {toAssetObj && (
              <p className="text-xs text-teal-600 mt-2 font-medium">
                Balance: {toAssetObj.amount} {toAssetObj.short_name}
              </p>
            )}
          </div>

          {/* Swap button */}
          <button
            onClick={submitSwap}
            disabled={swapping || !fromAsset || !toAsset || !amount}
            className="w-full py-3.5 rounded-2xl text-white font-bold text-base
              bg-gradient-to-r from-[#734aff] to-[#5b34ea] hover:opacity-90 transition
              disabled:opacity-50 disabled:cursor-not-allowed
              shadow-[0_8px_30px_rgba(110,72,255,0.4)]"
          >
            {swapping ? (
              <span className="flex items-center justify-center gap-2">
                <Spin size="small" /> Swapping…
              </span>
            ) : Swap}
          </button>
        </div>

        {/* ── RIGHT: Summary + How it works ── */}
        <div className="space-y-5 ">

          {/* Transaction summary card */}
          <div className="rounded-[26px] bg-[#1e293b] px-3 py-3 shadow-[0_22px_60px_rgba(15,23,42,0.9)] p-6">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-1">Transaction</p>
            <p className="text-base font-bold text-slate-100 mb-5">Summary</p>

            <div className="space-y-1">
              {summaryRows.map(({ label, value }) => (
                <div key={label} className="flex justify-between items-center py-3 border-b border-slate-700/60 last:border-0">
                  <p className="text-sm text-slate-500 font-medium">{label}</p>
                  <p className={`text-sm font-bold ${value ? 'text-slate-100' : 'text-slate-600'}`}>
                    {value || '—'}
                  </p>
                </div>
              ))}
            </div>

            {/* Rate badge */}
            {fromAssetObj && toAssetObj && (
              <div className="mt-4 rounded-2xl bg-[#6e48ff]/10 border border-[#6e48ff]/20 px-4 py-3 flex items-center justify-between">
                <p className="text-xs text-[#a78bfa] font-semibold">Exchange Rate</p>
                <p className="text-xs text-[#a78bfa] font-bold">
                  1 {fromAssetObj.short_name} → {toAssetObj.short_name}
                </p>
              </div>
            )}
          </div>

          {/* How it works card */}
          <div className="rounded-[26px] bg-[#1e293b] shadow-[0_22px_60px_rgba(15,23,42,0.9)] px-3 py-3">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-1">Guide</p>
            <p className="text-base font-bold text-slate-100 mb-5">How it works</p>

            <div className="space-y-4">
              {[
                { step: '01', text: 'Select the asset you want to send and enter an amount.' },
                { step: '02', text: 'Choose the asset you want to receive.' },
                { step: '03', text: 'Review the transaction summary on the right.' },
                { step: '04', text: 'Tap Swap — your transaction is submitted for approval.' },
              ].map(({ step, text }) => (
                <div key={step} className="flex items-start gap-4">
                  <span className="flex-shrink-0 w-8 h-8 rounded-xl bg-[#6e48ff]/15 flex items-center justify-center text-[#a78bfa] text-xs font-bold border border-[#6e48ff]/20">
                    {step}
                  </span>
                  <p className="text-sm text-slate-400 leading-relaxed pt-1">{text}</p>
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default SwapCrypto;